import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import useResize from '../../../../hooks/useResize'
import { VIEWPORT_LG } from '../../../../utils/constants'
import TipItem from '../../UploadVideoTalent/smallComponents/TipItem'
import WarningIcon from '../../../../icons/WarningIcon'
import Button from '../../../../components/Button'
import { getActiveMasterClasses, getConfig } from '../../../../services/masterClasses'
import { getCoursesList, getTalentQuestionProgress } from '../../../../services/Courses'
import { PRIVATE_ROUTES } from '../../../../Routes/routes'
import {
  findCourseProgress,
  isExamCompleted,
} from '../../../../utils/examResult'

const BRAND = '#01A76A'
const BRAND_LIGHT = '#00CF83'

// ─── Icon catalog (keys persisted in the master class model → SVG) ───────────

const iconProps = {
  width: '24',
  height: '24',
  viewBox: '0 0 24 24',
  fill: 'none',
  xmlns: 'http://www.w3.org/2000/svg',
}

const ICON_MAP = {
  terminal: (
    <svg {...iconProps}>
      <path
        d="M9.25059 7.11823H4.38978C4.02145 7.11823 3.6682 7.26455 3.40775 7.525C3.1473 7.78545 3.00098 8.1387 3.00098 8.50703V19.6175C3.00098 19.9858 3.1473 20.339 3.40775 20.5995C3.6682 20.8599 4.02145 21.0063 4.38978 21.0063H16.889C17.2573 21.0063 17.6106 20.8599 17.871 20.5995C18.1315 20.339 18.2778 19.9858 18.2778 19.6175V14.0622M3.00098 10.5902H12.0282"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.47266 17.5343L8.55586 15.451L6.47266 13.3678M10.6391 17.5343H12.7223M12.5501 7.76263C12.064 7.67791 12.064 6.97796 12.5501 6.89324C13.4131 6.74257 14.2119 6.3386 14.8448 5.73271C15.4776 5.12681 15.9159 4.34638 16.104 3.49067L16.1318 3.35596C16.2387 2.87404 16.9234 2.86988 17.0345 3.35179L17.0692 3.50873C17.2633 4.36112 17.7052 5.13705 18.3394 5.73881C18.9735 6.34057 19.7715 6.74127 20.6329 6.89046C21.1218 6.97518 21.1218 7.6793 20.6329 7.76541C19.7715 7.9146 18.9735 8.3153 18.3394 8.91706C17.7052 9.51883 17.2633 10.2948 17.0692 11.1471L17.0331 11.3027C16.9234 11.7832 16.2387 11.7804 16.1332 11.2985L16.104 11.1666C15.9159 10.3109 15.4776 9.53045 14.8448 8.92456C14.2119 8.31866 13.4131 7.91469 12.5501 7.76402"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  sparkles: (
    <svg {...iconProps}>
      <path
        d="M10.695 3.89999C7.22821 3.90629 5.412 3.99179 4.2519 5.151C3 6.4047 3 8.4198 3 12.45C3 16.4802 3 18.4953 4.2519 19.7481C5.5047 21 7.51891 21 11.5509 21C15.5811 21 17.5962 21 18.8481 19.7481C20.0091 18.5871 20.0937 16.7718 20.1 13.3041"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.1498 12.9C10.4982 4.67938 16.3212 2.34838 20.9814 3.14758C21.1695 5.87188 19.8357 6.89159 17.2977 7.36319C17.7882 7.87619 18.654 8.52059 18.5604 9.32519C18.4938 9.89939 18.105 10.1802 17.3256 10.7436C15.6192 11.9766 13.6428 12.7542 11.1498 12.9Z"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.2998 16.5C11.0998 11.55 12.8638 9.8724 14.6998 8.39999"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  api: (
    <svg {...iconProps}>
      <path
        d="M9 11.9685V5C9 4.20435 9.31607 3.44129 9.87868 2.87868C10.4413 2.31607 11.2044 2 12 2C12.7956 2 13.5587 2.31607 14.1213 2.87868C14.6839 3.44129 15 4.20435 15 5V6.003M15 12.0015V19C15 19.7956 14.6839 20.5587 14.1213 21.1213C13.5587 21.6839 12.7956 22 12 22C11.2044 22 10.4413 21.6839 9.87868 21.1213C9.31607 20.5587 9 19.7956 9 19V17.985"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M12 15H4.992C3.34 15 2 13.657 2 12C2 10.343 3.34 9 4.992 9H5.9945M12 9H18.9945C19.7909 8.99934 20.5549 9.31502 21.1185 9.87761C21.6821 10.4402 21.9992 11.2036 22 12C22 13.657 20.6545 15 18.9945 15H18.033"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
  user: (
    <svg {...iconProps}>
      <path
        d="M12 10C14.2091 10 16 8.20914 16 6C16 3.79086 14.2091 2 12 2C9.79086 2 8 3.79086 8 6C8 8.20914 9.79086 10 12 10Z"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21 22C21 17.0295 16.9705 13 12 13C7.0295 13 3 17.0295 3 22"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 22L14 19.5L12 13L10 19.5L12 22Z"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  camera: (
    <svg {...iconProps}>
      <path
        d="M15 10L19.553 7.724C19.7054 7.64784 19.8748 7.61188 20.045 7.61955C20.2152 7.62721 20.3806 7.67825 20.5256 7.76781C20.6706 7.85736 20.7902 7.98248 20.8733 8.13127C20.9563 8.28007 20.9999 8.44761 21 8.618V15.382C20.9999 15.5524 20.9563 15.7199 20.8733 15.8687C20.7902 16.0175 20.6706 16.1426 20.5256 16.2322C20.3806 16.3218 20.2152 16.3728 20.045 16.3805C19.8748 16.3881 19.7054 16.3522 19.553 16.276L15 14V10ZM3 8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H13C13.5304 6 14.0391 6.21071 14.4142 6.58579C14.7893 6.96086 15 7.46957 15 8V16C15 16.5304 14.7893 17.0391 14.4142 17.4142C14.0391 17.7893 13.5304 18 13 18H5C4.46957 18 3.96086 17.7893 3.58579 17.4142C3.21071 17.0391 3 16.5304 3 16V8Z"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  briefcase: (
    <svg {...iconProps}>
      <path
        d="M11.007 21H9.605C6.02 21 4.228 21 3.114 19.865C2 18.73 2 16.903 2 13.25C2 9.597 2 7.77 3.114 6.635C4.228 5.5 6.02 5.5 9.605 5.5H13.408C16.993 5.5 18.786 5.5 19.9 6.635C20.757 7.508 20.954 8.791 21 11"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20.017 20.023L22 22M16 5.5L15.9 5.19C15.405 3.65 15.158 2.88 14.569 2.44C13.979 2 13.197 2 11.63 2H11.367C9.802 2 9.019 2 8.43 2.44C7.84 2.88 7.593 3.65 7.098 5.19L7 5.5M21.053 17.526C21.0609 17.0579 20.9755 16.5929 20.8018 16.1581C20.6281 15.7234 20.3696 15.3276 20.0414 14.9937C19.7131 14.6599 19.3217 14.3948 18.8899 14.2138C18.4582 14.0328 17.9947 13.9396 17.5265 13.9396C17.0583 13.9396 16.5948 14.0328 16.1631 14.2138C15.7313 14.3948 15.3399 14.6599 15.0116 14.9937C14.6834 15.3276 14.4249 15.7234 14.2512 16.1581C14.0775 16.5929 13.9921 17.0579 14 17.526C14.0156 18.451 14.394 19.3329 15.0536 19.9815C15.7133 20.6301 16.6014 20.9936 17.5265 20.9936C18.4516 20.9936 19.3397 20.6301 19.9994 19.9815C20.659 19.3329 21.0374 18.451 21.053 17.526Z"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  calendar: (
    <svg {...iconProps}>
      <path
        d="M16.8 5.6H7.2C5.43269 5.6 4 7.03269 4 8.8V16.8C4 18.5673 5.43269 20 7.2 20H16.8C18.5673 20 20 18.5673 20 16.8V8.8C20 7.03269 18.5673 5.6 16.8 5.6Z"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.8 4V7.2M15.2 4V7.2M4 10.4H20"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  clock: (
    <svg {...iconProps}>
      <path
        d="M12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2C6.477 2 2 6.477 2 12C2 17.523 6.477 22 12 22Z"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M12.0039 6V12.005L16.2434 16.245"
        stroke={BRAND_LIGHT}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
}

const resolveIcon = (key) => ICON_MAP[key] || ICON_MAP.sparkles

const formatDate = (date) => {
  if (!date) return ''
  try {
    return new Date(`${date}T00:00:00`).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
  } catch (error) {
    return date
  }
}

// ─── Master class card ────────────────────────────────────────────────────────

const LockIcon = (props) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" {...props}>
    <rect x="5" y="11" width="14" height="9" rx="2.4" strokeLinecap="round" strokeLinejoin="round" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7.5a4 4 0 018 0V11" />
  </svg>
)

const MasterClassCard = ({ masterClass, isMobile, gate, onOpenGate, gateOpen, onCloseGate }) => {
  const hasUrl = Boolean(masterClass.registrationUrl)
  const locked = Boolean(gate)
  const enabled = hasUrl && !locked

  return (
    <div
      className="master-class-card d-flex flex-column h-100"
      onMouseEnter={locked ? onOpenGate : undefined}
      onMouseLeave={locked ? onCloseGate : undefined}
      onFocus={locked ? onOpenGate : undefined}
      onBlur={locked ? onCloseGate : undefined}
      style={{
        position: 'relative',
        width: isMobile ? '100%' : undefined,
        background: '#fff',
        borderRadius: 18,
        border: '1.5px solid #e8ecf0',
        boxShadow: '0 4px 18px rgba(0,0,0,0.06)',
        overflow: 'visible',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
      }}
    >
      <div
        style={{
          position: 'relative',
          background:
            masterClass.bannerImageUrl && masterClass.bannerImageUrl.length
              ? `linear-gradient(135deg, rgba(1,167,106,0.85), rgba(0,207,131,0.7)), url(${masterClass.bannerImageUrl}) center/cover`
              : `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
          padding: '18px 20px',
          color: '#fff',
          borderRadius: '18px 18px 0 0',
          overflow: 'hidden',
          filter: locked ? 'saturate(0.55) brightness(0.92)' : undefined,
        }}
      >
        <div className="d-flex align-items-start justify-content-between gap-2">
          <span
            style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.22)',
              border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: 20,
              padding: '4px 12px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.03em',
            }}
          >
            {masterClass.topic || 'Master class'}
          </span>

          {locked && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                background: 'rgba(15,23,42,0.35)',
                border: '1px solid rgba(255,255,255,0.35)',
                borderRadius: 20,
                padding: '4px 10px',
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.02em',
                flexShrink: 0,
                backdropFilter: 'blur(2px)',
              }}
            >
              <LockIcon />
              Bloqueada
            </span>
          )}
        </div>
        <h3 className="fontSize20 fw-bold mt-2 mb-0">{masterClass.title}</h3>
      </div>

      <div
        className="p-4 d-flex flex-column flex-grow-1"
        style={{ opacity: locked ? 0.92 : 1 }}
      >
        {masterClass.description && (
          <p className="fontSize14 text-on-bg-primary" style={{ lineHeight: 1.6 }}>
            {masterClass.description}
          </p>
        )}

        <div className="d-flex flex-column gap-2 mt-2 mb-3">
          <TipItem
            icon={resolveIcon('calendar')}
            text={`${formatDate(masterClass.date)}${
              masterClass.time ? ` · ${masterClass.time}` : ''
            }${masterClass.timezone ? ` (${masterClass.timezone})` : ''}`}
          />
          {masterClass.durationMinutes ? (
            <TipItem
              icon={resolveIcon('clock')}
              text={`Duración: ${masterClass.durationMinutes} min`}
            />
          ) : null}
          {masterClass.modality ? (
            <TipItem icon={resolveIcon('camera')} text={masterClass.modality} />
          ) : null}
        </div>

        {Array.isArray(masterClass.learnings) && masterClass.learnings.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            {masterClass.learnings.map((learning, index) => (
              <span
                key={index}
                style={{
                  background: '#CFF2DF',
                  border: `1px solid ${BRAND}`,
                  color: BRAND,
                  borderRadius: 12,
                  padding: '4px 10px',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {learning.text}
              </span>
            ))}
          </div>
        )}

        {locked ? (
          <GatePopover
            gate={gate}
            isOpen={gateOpen}
            onOpen={onOpenGate}
            onClose={onCloseGate}
          />
        ) : (
          <Link
            className="text-decoration-none mt-auto text-center"
            to={enabled ? masterClass.registrationUrl : '#'}
            target={enabled ? '_blank' : '_self'}
            rel="noreferrer"
            style={{
              background: enabled
                ? `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`
                : '#e9ecef',
              color: enabled ? '#fff' : '#9aa0a6',
              borderRadius: 10,
              padding: '11px 0',
              fontSize: 14,
              fontWeight: 700,
              opacity: enabled ? 1 : 0.85,
              pointerEvents: enabled ? 'auto' : 'none',
              cursor: enabled ? 'pointer' : 'not-allowed',
              transition: 'filter 0.2s',
            }}
          >
            {hasUrl ? 'Registrarme' : 'Próximamente'}
          </Link>
        )}
      </div>
    </div>
  )
}

// ─── Popover de bloqueo ──────────────────────────────────────────────────────
// Dos motivos posibles, evaluados en este orden de prioridad (ver
// gateByMasterClassId): 1) perfil de talento incompleto (bloquea TODAS las
// master classes), 2) examen del curso vinculado (courseId) sin aprobar.

const GATE_COPY = {
  profile: {
    triggerLabel: 'Completa tu perfil',
    body: 'Para inscribirte en las master classes primero debes completar tu información de perfil en Hutrit.',
    cta: 'Completar perfil',
  },
  exam: {
    triggerLabel: 'Requiere examen aprobado',
    cta: 'Ir al examen del curso',
  },
}

const GatePopover = ({ gate, isOpen, onOpen, onClose }) => {
  const navigate = useNavigate()
  const copy = GATE_COPY[gate.type]

  const handleCta = () => {
    onClose()
    if (gate.type === 'profile') {
      navigate('/talent-information')
      return
    }
    navigate(PRIVATE_ROUTES.academy, {
      state: {
        openExamCourse: { id: gate.courseId, title: gate.courseTitle },
      },
    })
  }

  const goToClasses = () => {
    onClose()
    navigate(PRIVATE_ROUTES.academy, {
      state: {
        openCourseClasses: { id: gate.courseId, title: gate.courseTitle },
      },
    })
  }

  return (
    <div className="mt-auto" style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={onOpen}
        className="w-100"
        style={{
          background: '#f1f3f5',
          color: '#697077',
          border: '1px solid #e3e7ea',
          borderRadius: 10,
          padding: '11px 14px',
          fontSize: 14,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          cursor: 'pointer',
          transition: 'background 0.2s ease',
        }}
      >
        <LockIcon width="14" height="14" />
        {copy.triggerLabel}
      </button>

      {isOpen && (
        <>
          <div
            role="dialog"
            style={{
              position: 'absolute',
              zIndex: 41,
              bottom: '100%',
              left: 0,
              right: 0,
              paddingBottom: 10,
              background: 'transparent',
              animation: 'examGateFadeIn 0.18s ease',
            }}
          >
          <div
            style={{
              background: '#fff',
              borderRadius: 14,
              border: '1px solid #e8ecf0',
              boxShadow: '0 16px 36px rgba(15,23,42,0.16)',
              padding: 16,
            }}
          >
            <div className="d-flex align-items-start gap-2 mb-2">
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  borderRadius: 9,
                  background: 'rgba(1,167,106,0.1)',
                  color: BRAND,
                }}
              >
                <LockIcon width="14" height="14" />
              </span>
              <p
                className="mb-0"
                style={{ fontSize: 13.5, lineHeight: 1.5, color: '#374151' }}
              >
                {gate.type === 'profile' ? (
                  copy.body
                ) : (
                  <>
                    Para inscribirte en esta master class primero debes
                    completar el curso{' '}
                    <strong style={{ color: '#0f172a' }}>
                      “{gate.courseTitle}”
                    </strong>{' '}
                    y aprobar su examen.
                  </>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={handleCta}
              className="w-100"
              style={{
                background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                padding: '10px 14px',
                fontSize: 13.5,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {copy.cta}
            </button>
            {gate.type === 'exam' && (
              <button
                type="button"
                onClick={goToClasses}
                className="w-100 mt-2"
                style={{
                  background: '#fff',
                  color: BRAND,
                  border: `1.5px solid ${BRAND}`,
                  borderRadius: 10,
                  padding: '9px 14px',
                  fontSize: 13.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Ir a las clases del curso
              </button>
            )}
          </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes examGateFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}

// ─── Skeletons ─────────────────────────────────────────────────────────────

const MasterClassCardSkeleton = () => (
  <div
    className="d-flex flex-column h-100"
    style={{
      background: '#fff',
      borderRadius: 18,
      border: '1.5px solid #e8ecf0',
      overflow: 'hidden',
    }}
  >
    <div style={{ padding: '18px 20px' }}>
      <Skeleton width={90} height={20} borderRadius={20} />
      <div className="mt-2">
        <Skeleton width="80%" height={22} />
      </div>
    </div>
    <div className="p-4 d-flex flex-column flex-grow-1 gap-2">
      <Skeleton count={2} />
      <div className="mt-2">
        <Skeleton width="70%" height={16} />
      </div>
      <Skeleton width="55%" height={16} />
      <div className="mt-3">
        <Skeleton height={40} borderRadius={10} />
      </div>
    </div>
  </div>
)

const MasterClassViewSkeleton = ({ isMobile }) => (
  <div>
    <div
      style={{
        borderRadius: 20,
        padding: isMobile ? '28px 20px' : '44px 48px',
        background: '#f2f4f3',
      }}
    >
      <Skeleton width={isMobile ? '80%' : 420} height={isMobile ? 28 : 44} />
      <div className="mt-3 mx-auto" style={{ maxWidth: 760 }}>
        <Skeleton count={2} />
      </div>
    </div>

    <div className="border border-2 rounded gap-3 px-3 py-4 w-100 mt-3">
      <Skeleton width={220} height={22} className="d-block mx-auto" />
      <div className="mt-2">
        <Skeleton height={60} borderRadius={8} />
      </div>
      <div
        className="w-100 mt-4"
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile
            ? '1fr'
            : 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 20,
        }}
      >
        {Array.from({ length: 3 }).map((_, index) => (
          <MasterClassCardSkeleton key={index} />
        ))}
      </div>
    </div>
  </div>
)

// ─── Main presentational view ────────────────────────────────────────────────

const MasterClassView = ({ userData }) => {
  const viewportSize = useResize()
  const navigate = useNavigate()
  const isMobile = viewportSize[0] <= VIEWPORT_LG
  const canRegister = Boolean(userData?.userInfo?.steps_completed)

  const [config, setConfig] = useState({})
  const [masterClasses, setMasterClasses] = useState([])
  const [courses, setCourses] = useState([])
  const [examProgress, setExamProgress] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [openGateId, setOpenGateId] = useState(null)

  useEffect(() => {
    let active = true
    Promise.all([
      getConfig(),
      getActiveMasterClasses(),
      getCoursesList(),
      getTalentQuestionProgress(),
    ]).then(([cfg, list, coursesResult, progressResult]) => {
      if (!active) return
      setConfig(cfg || {})
      setMasterClasses(list || [])
      setCourses(coursesResult?.res?.courses || [])
      setExamProgress(progressResult?.res?.progress || [])
      setIsLoading(false)
    })
    return () => {
      active = false
    }
  }, [])

  // Orden de prioridad para decidir si una master class está bloqueada:
  //   Curso vinculado (`courseId`) con examen sin aprobar -> bloquea solo
  //   esa master class. Sin `courseId` -> no hay examen que exigir, queda
  //   abierta. El perfil incompleto ya no bloquea el registro.
  const gateByMasterClassId = useMemo(() => {
    const map = {}
    masterClasses.forEach((masterClass) => {
      if (!masterClass.courseId) return
      const matchedCourse = courses.find(
        (course) => Number(course.id) === Number(masterClass.courseId)
      )
      if (!matchedCourse) return
      const progressEntry = findCourseProgress(examProgress, matchedCourse.id)
      const passed = progressEntry ? isExamCompleted(progressEntry) : false
      if (!passed) {
        map[masterClass.id] = {
          type: 'exam',
          courseId: matchedCourse.id,
          courseTitle: matchedCourse.title,
        }
      }
    })
    return map
  }, [masterClasses, courses, examProgress])

  // Learnings agregados de todas las master classes activas para la sección superior.
  const aggregatedLearnings = useMemo(() => {
    const seen = new Set()
    const list = []
    masterClasses.forEach((masterClass) => {
      const learnings = masterClass.learnings || []
      learnings.forEach((learning) => {
        const key = learning.text?.toLowerCase()
        if (key && !seen.has(key)) {
          seen.add(key)
          list.push(learning)
        }
      })
    })
    return list
  }, [masterClasses])

  const itemSize = isMobile ? '100%' : '320px'

  if (isLoading) {
    return (
      <div className="mt-0 mt-lg-5 pt-0 pt-lg-5 pb-0 pb-lg-5 px-lg-0 px-2">
        <MasterClassViewSkeleton isMobile={isMobile} />
      </div>
    )
  }

  return (
    <div className="mt-0 mt-lg-5 pt-0 pt-lg-5 pb-0 pb-lg-5 px-lg-0 px-2">
      <style>{`
        @media (hover: hover) and (pointer: fine) {
          .master-class-card:hover {
            transform: translateY(-6px);
            box-shadow: 0 14px 32px rgba(1,167,106,0.18);
          }
        }
      `}</style>

      {/* Hero */}
      <div
        style={{
          background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
          borderRadius: 20,
          padding: isMobile ? '28px 20px' : '44px 48px',
          color: '#fff',
          textAlign: 'center',
        }}
      >
        <h1 className={`${isMobile ? 'fontSize24' : 'fontSize40'} fw-bold m-0`}>
          {config.heroTitle}
        </h1>
        <p
          className={`${isMobile ? 'fontSize14' : 'fontSize20'} mt-3 mb-0 mx-auto`}
          style={{ maxWidth: 760, opacity: 0.92 }}
        >
          {config.heroSubtitle}
        </p>
      </div>

      {/* Learnings */}
      {aggregatedLearnings.length > 0 && (
        <div className="text-on-bg-primary d-flex flex-column align-items-center border border-2 rounded gap-3 px-3 py-4 w-100 mt-4">
          <span
            className={`${isMobile ? 'fontSize14' : 'fontSize20'} fw-bolder text-center`}
          >
            {config.learningsSectionTitle}
          </span>
          <div className="d-flex flex-wrap justify-content-center mt-2 gap-3 w-100">
            {aggregatedLearnings.map((learning, index) => (
              <div key={index} style={{ width: itemSize }}>
                <TipItem icon={resolveIcon(learning.icon)} text={learning.text} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Schedule / cards */}
      <div className="text-on-bg-primary d-flex flex-column align-items-center border border-2 rounded gap-3 px-3 py-4 w-100 mt-3">
        <span
          className={`${isMobile ? 'fontSize14' : 'fontSize20'} fw-bolder text-center`}
        >
          {config.scheduleSectionTitle}
        </span>
        <span className="fontSize14 text-center">{config.scheduleSubtitle}</span>

        {!canRegister ? (
          <div className="p-3 rounded border border-on-bg-danger bg-bg-danger d-flex flex-lg-row flex-column align-items-center justify-content-lg-between w-100">
            <div className="d-flex align-items-center gap-2">
              <WarningIcon fill="#EA3D29" width="32px" height="32px" />
              <div className="d-flex flex-column gap-1">
                <span className={`${isMobile ? 'fontSize14' : 'fontSize20'} fw-bolder`}>
                  Todavía no has finalizado tu perfil
                </span>
                <span className="text-on-bg-primary fontSize14">
                  Para registrarte en las master classes debes completar tu perfil en Hutrit
                </span>
              </div>
            </div>
            <Button onClick={() => navigate('/talent-information')}>
              Completar perfil
            </Button>
          </div>
        ) : (
          <div className="p-3 rounded border border-bg-blue bg-bg-blue-secondary d-flex align-items-center justify-content-center w-100">
            <span className="fontSize14 text-center">{config.warningCopy}</span>
          </div>
        )}

        {masterClasses.length === 0 ? (
          <div className="text-center py-4">
            <span className="fontSize14 text-on-bg-primary">
              No hay master classes disponibles por el momento. ¡Vuelve pronto!
            </span>
          </div>
        ) : (
          <div
            className="w-100 mt-2"
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile
                ? '1fr'
                : 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: 20,
            }}
          >
            {masterClasses.map((masterClass) => (
              <MasterClassCard
                key={masterClass.id}
                masterClass={masterClass}
                isMobile={isMobile}
                gate={gateByMasterClassId[masterClass.id]}
                gateOpen={openGateId === masterClass.id}
                onOpenGate={() => setOpenGateId(masterClass.id)}
                onCloseGate={() => setOpenGateId(null)}
              />
            ))}
          </div>
        )}
      </div>

      {/* <div className="d-flex align-items-center justify-content-center mt-4 pb-5">
        <Button
          className="btn-bg-CTA-primary text-on-bg-primary p-3"
          onClick={() => navigate('/hutrit-club-upload-video')}
          disabled={!canRegister}
        >
          Continuar con el reto
        </Button>
      </div> */}
    </div>
  )
}

export default MasterClassView
